// src/components/AppTokenMng.tsx - Versione di rollback funzionante

import React from "react";
import {RootState, useAppDispatch} from "../redux/store";
import AppComposer from "./AppComposer";
import {
    utenteTokenRefresh,
    UtenteLogged,
    getUtente,
} from "./authentication/authenticationSlice";
import {ErrorStatusTypes, ErrorsStack} from "./common/errorsDeclarations";

import {useSelector} from "react-redux";
import {resetAnagrafiche, fetchAnagrafiche} from "./fetchAnagrafiche";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import {UserStored} from "./userPreference/userPreferenceSlice";

const AppTokenMng = () => {
    const dispatch = useAppDispatch();

    // ✅ BACK TO BASICS: Usa solo stato locale semplice
    const [isInitializing, setIsInitializing] = React.useState(true);
    const [hasAttemptedRestore, setHasAttemptedRestore] = React.useState(false);

    // ✅ TUTTI gli useSelector come prima
    const minutiRefresh = useSelector<RootState, number>(
        (state) => state.authentication.minutiRefresh
    );

    const minutiRefreshAnagrafiche = useSelector<RootState, number>(
        (state) => state.authentication.minutiRefreshAnagrafiche
    );

    const utente = useSelector<RootState, UtenteLogged>(
        (state) => state.authentication.utenteLogged
    );

    const userStored = useSelector<RootState, UserStored|undefined>(
        (state) => state.userPreferenceState.userStored
    );

    const tokenStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.authentication.token.errorsStack.status
    );

    const access_token = useSelector<RootState, string | undefined>(
        (state) => state.userPreferenceState?.userStored?.access
    );

    const comuniUltimoAggiornamento = useSelector<RootState, string | undefined>(
        (state) => state.comuniState.comuni?.ultimo_aggiornamento
    );

    // ✅ Helper functions semplici
    const isTokenExpired = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            return payload.exp < currentTime;
        } catch {
            return true;
        }
    };

    const isRefreshTokenValid = (): boolean => {
        if (!userStored?.refresh || !userStored?.refreshExpiresAt) {
            return false;
        }
        return new Date(userStored.refreshExpiresAt) > new Date();
    };

    // ✅ SEMPLIFICATO: Restore session useEffect
    React.useEffect(() => {

        const restoreSession = async () => {
            if (!userStored?.access || hasAttemptedRestore) {
                setIsInitializing(false);
                return;
            }

            setHasAttemptedRestore(true);

            try {
                if (utente.isLogged && utente.id) {
                    setIsInitializing(false);
                    return;
                }

                const isAccessExpired = isTokenExpired(userStored.access);
                const isRefreshValid = isRefreshTokenValid();


                if (isAccessExpired && !isRefreshValid) {
                    setIsInitializing(false);
                    return;
                }

                if (isAccessExpired && isRefreshValid) {
                    await dispatch(utenteTokenRefresh(userStored)).unwrap();
                } else {
                    await dispatch(getUtente()).unwrap();
                }


            } catch (error) {
                console.log('🔍 Restore failed:', error);
            } finally {
                setTimeout(() => {
                    setIsInitializing(false);
                }, 100);
            }
        };

        const timer = setTimeout(restoreSession, 100);
        return () => clearTimeout(timer);
    }, [userStored?.access, hasAttemptedRestore, utente.isLogged, utente.id, dispatch]);

    // ✅ RESTO DEGLI USEEFFECT come prima...
    React.useEffect(() => {
        if (isInitializing || !access_token || !utente.isLogged || !userStored) return;

        const timeRefresh = minutiRefresh * 60 * 1000;

        const shouldRefreshImmediately = (): boolean => {
            if (!access_token) return false;
            try {
                const payload = JSON.parse(atob(access_token.split('.')[1]));
                const tokenExp = payload.exp * 1000;
                const now = Date.now();
                const timeUntilExp = tokenExp - now;
                return timeUntilExp < 5 * 60 * 1000;
            } catch {
                return true;
            }
        };

        const isRefreshExpired = (): boolean => {
            if (!userStored.refreshExpiresAt) return false;
            return new Date(userStored.refreshExpiresAt) < new Date();
        };

        // Refresh immediato al mount se il token sta per scadere
        if (shouldRefreshImmediately()) {
            dispatch(utenteTokenRefresh(userStored));
        }

        // Refresh periodico (solo quando il tab è visibile)
        const interval = setInterval(() => {
            if (document.hidden) return;

            if (isRefreshExpired()) {
                clearInterval(interval);
                return;
            }

            dispatch(utenteTokenRefresh(userStored));
        }, timeRefresh);

        // Refresh al ritorno sul tab dopo periodo in background
        const handleVisibilityChange = () => {
            if (document.hidden) return;

            if (isRefreshExpired()) return;

            if (shouldRefreshImmediately()) {
                dispatch(utenteTokenRefresh(userStored));
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isInitializing, utente.isLogged, access_token, userStored?.refreshExpiresAt]);

    React.useEffect(() => {
        if (isInitializing || !utente.isLogged) return;

        resetAnagrafiche(dispatch);
        fetchAnagrafiche(dispatch, utente, 200);

        const timeRefresh = minutiRefreshAnagrafiche * 60 * 1000;

        const intervalloAggiornamentoAnagrafiche = setInterval(() => {
            if (!document.hidden) {
                fetchAnagrafiche(dispatch, utente, 5000, comuniUltimoAggiornamento);
            }
        }, timeRefresh);

        return () => clearInterval(intervalloAggiornamentoAnagrafiche);
    }, [isInitializing, utente.isLogged]);

    // ✅ CALCOLO stato loading
    const isAuthenticating = isInitializing || (!utente.isLogged && (
        tokenStatus === ErrorStatusTypes.PENDING ||
        utente.errorsStack.status === ErrorStatusTypes.PENDING
    ));

    const getLoadingMessage = () => {
        if (isInitializing) {
            return userStored?.access
                ? "Ripristino sessione..."
                : "Inizializzazione...";
        }
        return "Verifica autenticazione...";
    };

    return (
        <>
            {isAuthenticating ? (
                <Box sx={{width: "100%"}}>
                    <Stack
                        sx={{height: 400}}
                        justifyContent="center"
                        alignItems="center"
                        spacing={{xs: 1, sm: 2, md: 4}}
                    >
                        <Card variant="outlined" sx={{width: "50%"}}>
                            <CardContent>
                                <Typography variant="h3" gutterBottom>
                                    {getLoadingMessage()}
                                </Typography>
                                <LinearProgress/>
                                <Typography variant="body2" sx={{mt: 2}} color="text.secondary">
                                    Attendere prego...
                                    {userStored?.username && (
                                        <><br/>Utente: {userStored.username}</>
                                    )}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Stack>
                </Box>
            ) : (
                <>
                    <AppComposer/>
                </>
            )}
        </>
    );
};

export default AppTokenMng;